import { IShareContentRecipient } from '@tableau/share-dialog';
import _ from 'underscore';

import { Set } from '~src/commonInfra/utils/Set';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { ITypedIdWithName } from '~src/httpClient/serverApi/types/ITypedId';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { ISiteUser } from '~src/userManagement/users/serverApi/types/ISiteUser';
import { IUser } from '~src/userManagement/users/serverApi/types/IUser';

import { EffectivePermissions } from '../requests/EffectivePermissions';
import { ExplicitPermissionsResult, getExplicitPermissionsForAuthorizables } from '../requests/ExplicitPermissions';
import { GetActionableObjectsForAction } from '../requests/GetActionableObjectsForAction';
import { getAuthorizableType } from '../types/AuthorizableType';
import { IGranteePermissions } from '../types/IGranteePermissions';
import { IPermission, Permission } from '../types/Permission';
import { PermissionsEditabilityLogic } from './PermissionsEditabilityLogic';

export module GrantAccessPermissionsHelper {
  type CanGrantPermissionType = {
    canGrantPermissions: boolean;
    /* only included if the content does not control its own permissions */
    sourceAuthorizable?: ITypedIdWithName;
  }

  export async function getUsersWithoutReadPermissionsForContent(shareRecipients: IShareContentRecipient[], content: IContentInfo<ExplorableItemType>) {
    const userIds: string[] = shareRecipients.map(recipient => recipient.recipientId.toString());
    const effectivePermissions: ListResult<IGranteePermissions> = await EffectivePermissions.getEffectivePermissionsWithoutGettingSiteUsers(
      userIds,
      { type: content.contentType, ids: [content.id] }
    );
    const usersWithoutReadPermissions: (IUser | ISiteUser)[] = [];
    for (let perms of effectivePermissions.items) {
      const permissionsForContent: _.Dictionary<IPermission> = perms.permissionsByContentType[content.contentType];
      if (permissionsForContent?.read?.permission !== Permission.Allowed) {
        usersWithoutReadPermissions.push(perms.user);
      }
    }
    return usersWithoutReadPermissions;
  }

  export async function canUserGrantPermission(content: IContentInfo<ExplorableItemType>): Promise<CanGrantPermissionType> {
    const authorizableType = getAuthorizableType(content.contentType);
    const explicitPermissions: ExplicitPermissionsResult = await getExplicitPermissionsForAuthorizables({type: authorizableType, ids: [content.id]}, false);
    if (explicitPermissions.errors && explicitPermissions.errors.length) {
      // if the user cannot get explicit permissions, then they are not allowed to set permissions
      return {canGrantPermissions: false};
    }
    const authorizable = Set.fromIdItemArray([content]);
    const isEditable: boolean = PermissionsEditabilityLogic.getPermissionsEditability(authorizable, authorizableType, explicitPermissions.sourceAuthorizablesById).editable;
    if (isEditable) {
      // if the user can get explicit permissions AND the permissions are editable, it means that they can set permissions
      return {canGrantPermissions: true};
    }
    const controllingContent: ITypedIdWithName = explicitPermissions.sourceAuthorizablesById[content.id];
    const actionableItems = await GetActionableObjectsForAction.editPermissions({type: controllingContent.type, ids: [controllingContent.id]});
    if (actionableItems.ids.includes(controllingContent.id)) {
      return {canGrantPermissions: true, sourceAuthorizable: controllingContent};
    }
    return {canGrantPermissions: false};
  }
}
